import { FC, memo, useMemo, useState, useEffect } from "react";
import { Center, Spinner, Stack, FormControl, RadioGroup, Radio, HStack, Box, Text, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText } from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import axios from "axios";
import appconfig from "../../config.json";
import { useLoginUser } from "../../hooks/useLoginUser";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

type Props = {
  stateMachineArn?: string;
};

interface MetricDataResult {
  Id: string;
  Label: string;
  Timestamps: string[];
  Values: number[];
  StatusCode: string;
}

export const StepFunctionMetricDrawer: FC<Props> = memo((props) => {
  const { stateMachineArn } = props;
  const { tokenRefresh } = useLoginUser();
  const [metricDataResults, setMetricDataResults] = useState<MetricDataResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [periodset, setPeriodset] = useState<"minit" | "5minits" | "hour" | "day">("hour");
  const [timeRange, setTimeRange] = useState<"1h" | "6h" | "24h" | "7d">("24h");

  const MANAGEMENT_API_URL: string = appconfig["IOT_MANAGEMENT_API_URL"];

  useEffect(() => {
    if (!stateMachineArn) return;

    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const token = await tokenRefresh();
        const endDate = new Date();
        const startDate = new Date();
        let period: number = 3600;

        if (periodset === "minit") period = 60;
        else if (periodset === "5minits") period = 300;
        else if (periodset === "hour") period = 3600;
        else if (periodset === "day") period = 86400;

        // 時間範囲の設定
        if (timeRange === "1h") startDate.setHours(endDate.getHours() - 1);
        else if (timeRange === "6h") startDate.setHours(endDate.getHours() - 6);
        else if (timeRange === "24h") startDate.setDate(endDate.getDate() - 1);
        else if (timeRange === "7d") startDate.setDate(endDate.getDate() - 7);

        // StepFunctions ARNからステートマシン名を抽出
        const stateMachineName = stateMachineArn.split(":").pop();

        // StateMachineNameディメンションを追加してAPIリクエスト
        const params = new URLSearchParams({
          namespace: "AWS/States",
          starttime: startDate.toISOString(),
          endtime: endDate.toISOString(),
          period: period.toString(),
          dimensionName: "StateMachineArn",
          dimensionValue: stateMachineArn,
        });

        const response = await axios.get(
          `${MANAGEMENT_API_URL}/metricdata?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // 指定されたStateMachineのメトリックのみをフィルタリング
        const targetMetrics = ['ExecutionsSucceeded', 'ExecutionsFailed', 'ExecutionsStarted'];
        const filteredResults = (response.data.MetricDataResults || [])
          .filter((result: MetricDataResult) => {
            // Label に完全一致する stateMachineArn が含まれ、かつ主要メトリクスのいずれかを含む
            // かつ、Values配列が空でないもののみ
            return result.Label?.includes(stateMachineArn) && 
                   targetMetrics.some(metric => result.Label?.includes(metric)) &&
                   result.Values && result.Values.length > 0;
          })
          .map((result: MetricDataResult) => {
            // LabelからARNを除去してメトリック名のみを抽出
            const metricName = targetMetrics.find(metric => result.Label?.includes(metric));
            return {
              ...result,
              Label: metricName || result.Label
            };
          });
        
        setMetricDataResults(filteredResults);
      } catch (error) {
        console.error("Failed to fetch StepFunctions metrics:", error);
        setMetricDataResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [stateMachineArn, periodset, timeRange, tokenRefresh, MANAGEMENT_API_URL]);

  const chartData = useMemo(() => {
    if (!metricDataResults || metricDataResults.length === 0) {
      return { labels: [], datasets: [] };
    }

    const timestamps = [...metricDataResults[0].Timestamps];
    const sortedTimestamps = timestamps.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const labels = sortedTimestamps.map(timestamp => new Date(timestamp).toLocaleString('ja-JP'));

    const colors = [
      { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.5)' },
      { border: 'rgb(53, 162, 235)', background: 'rgba(53, 162, 235, 0.5)' },
      { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.5)' },
      { border: 'rgb(255, 205, 86)', background: 'rgba(255, 205, 86, 0.5)' },
    ];

    const datasets = metricDataResults.map((metric, index) => {
      const colorIndex = index % colors.length;
      const sortedData = sortedTimestamps.map(ts => {
        const originalIndex = metric.Timestamps.findIndex(t => t === ts);
        return originalIndex !== -1 ? metric.Values[originalIndex] : null;
      });

      return {
        label: metric.Label || `メトリクス ${index + 1}`,
        data: sortedData,
        borderColor: colors[colorIndex].border,
        backgroundColor: colors[colorIndex].background,
      };
    });

    return { labels, datasets };
  }, [metricDataResults]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const, display: true },
      title: { display: true, text: 'StepFunctions実行回数', font: { size: 16 } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    scales: {
      x: { ticks: { maxRotation: 45, minRotation: 45, autoSkip: true } },
    },
    interaction: { mode: 'nearest' as const, axis: 'x' as const, intersect: false },
  }), []);

  const metricSummary = useMemo(() => {
    if (!metricDataResults || metricDataResults.length === 0) return [];

    return metricDataResults.map(metric => {
      const total = metric.Values.reduce((sum, val) => sum + val, 0);
      const avg = metric.Values.length > 0 ? total / metric.Values.length : 0;
      const max = metric.Values.length > 0 ? Math.max(...metric.Values) : 0;

      return {
        label: metric.Label || 'Unknown',
        total: Math.round(total),
        average: Math.round(avg * 100) / 100,
        max: Math.round(max),
      };
    });
  }, [metricDataResults]);

  if (!stateMachineArn) {
    return (
      <Center h="50vh">
        <Text color="gray.500">StepFunctions ARNが設定されていません</Text>
      </Center>
    );
  }

  return (
    <>
      <Stack spacing={4} mb={4}>
        <FormControl>
          <Text fontSize="sm" fontWeight="bold" mb={2}>時間範囲</Text>
          <RadioGroup
            onChange={(nextValue: "1h" | "6h" | "24h" | "7d") => setTimeRange(nextValue)}
            value={timeRange}
          >
            <HStack flexWrap="wrap" spacing="5">
              <Radio value="1h">過去1時間</Radio>
              <Radio value="6h">過去6時間</Radio>
              <Radio value="24h">過去24時間</Radio>
              <Radio value="7d">過去7日間</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
        
        <FormControl>
          <Text fontSize="sm" fontWeight="bold" mb={2}>集計間隔</Text>
          <RadioGroup
            onChange={(nextValue: "minit" | "5minits" | "hour" | "day") => setPeriodset(nextValue)}
            value={periodset}
          >
            <HStack flexWrap="wrap" spacing="5">
              <Radio value="minit">1分間隔</Radio>
              <Radio value="5minits">5分間隔</Radio>
              <Radio value="hour">１時間間隔</Radio>
              <Radio value="day">１日間隔</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
      </Stack>

      {loading ? (
        <Center h="50vh">
          <Spinner color="teal.500" />
        </Center>
      ) : (
        <>
          <Stack bg="white" width="full" p="5" className="mixed-chart" h="50VH" mb={4}>
            <Line data={chartData} options={chartOptions} />
          </Stack>

          {metricSummary.length > 0 && (
            <Box bg="gray.50" p={4} borderRadius="md">
              <Text fontSize="lg" fontWeight="bold" mb={3}>メトリック統計</Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                {metricSummary.map((summary, index) => (
                  <Stat key={index} bg="white" p={3} borderRadius="md" boxShadow="sm">
                    <StatLabel fontSize="sm" color="gray.600">{summary.label}</StatLabel>
                    <StatNumber fontSize="2xl" color="teal.600" textAlign="right">{summary.total.toLocaleString()}</StatNumber>
                    <StatHelpText fontSize="xs" textAlign="right">
                      平均: {summary.average.toLocaleString()} | 最大: {summary.max.toLocaleString()}
                    </StatHelpText>
                  </Stat>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </>
      )}
    </>
  );
});
